import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FlexContainer } from '../styled-ui';

const LoadingContainer = styled.div`
  padding: 1.5rem;
  border: 1px solid var(--gray-border-lighter);
  border-radius: var(--medium-border-radius);
`;

const LoadingMessage = styled.span`
  display: block;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--black-main);
`;

const PROGRESS_CONFIG = {
  updateInterval: 1500,
  maxValue: 92,
  minValue: 1,
  incrementMin: 1,
  incrementMax: 8,
} as const;

type AILoadingProgressProps = {
  isLoading: boolean;
  messages: readonly string[];
  progressBarHeight?: number;
  progressBarColor?: string;
  className?: string;
  isSpinner?: boolean;
};

export const AILoadingProgress: React.FC<AILoadingProgressProps> = ({
  isLoading,
  messages,
  progressBarHeight = 5,
  progressBarColor = 'var(--red-main)',
  className,
  isSpinner = false,
}) => {
  const [value, setValue] = useState<number>(PROGRESS_CONFIG.minValue);
  const [messageIndex, setMessageIndex] = useState<number>(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasLoadingRef = useRef<boolean>(false);

  const currentMessage = messages[messageIndex] || messages[0];

  // Manage progress interval
  useEffect(() => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (isLoading && messages.length > 0) {
      // Only reset progress when loading starts (transition from false to true)
      if (!wasLoadingRef.current) {
        setValue(PROGRESS_CONFIG.minValue);
      }

      // Set up progress interval
      progressIntervalRef.current = setInterval(() => {
        setValue((prevValue) => {
          if (prevValue < PROGRESS_CONFIG.maxValue) {
            const increment =
              Math.floor(
                Math.random() *
                  (PROGRESS_CONFIG.incrementMax -
                    PROGRESS_CONFIG.incrementMin +
                    1),
              ) + PROGRESS_CONFIG.incrementMin;
            return Math.min(prevValue + increment, PROGRESS_CONFIG.maxValue);
          }
          return prevValue;
        });
      }, PROGRESS_CONFIG.updateInterval);
    } else if (!isLoading) {
      setValue(100);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isLoading, messages]);

  // Manage message cycling separately - cycles through all messages continuously
  useEffect(() => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }

    if (isLoading && messages.length > 0) {
      // Only reset message index when loading starts (transition from false to true)
      if (!wasLoadingRef.current) {
        setMessageIndex(0);
      }

      // Cycle through messages continuously
      messageIntervalRef.current = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
      }, PROGRESS_CONFIG.updateInterval);
    }

    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    };
  }, [isLoading, messages]);

  // Track loading state transitions
  useEffect(() => {
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  if (!isLoading) {
    return null;
  }

  return (
    <LoadingContainer className={className}>
      {isSpinner ? (
        <FlexContainer
          direction="column"
          align="center"
          justify="center"
          gap={3}
        >
          <ProgressSpinner style={{ width: '32px', height: '32px' }} />
          <LoadingMessage role="status" aria-live="polite">
            {currentMessage}
          </LoadingMessage>
        </FlexContainer>
      ) : (
        <>
          <ProgressBar
            value={value}
            showValue={false}
            color={progressBarColor}
            style={{ height: progressBarHeight }}
            aria-label="AI processing progress"
          />
          <LoadingMessage role="status" aria-live="polite">
            {currentMessage}
          </LoadingMessage>
        </>
      )}
    </LoadingContainer>
  );
};
